import * as React from "react";
import { Icon } from "../actions/Icon";

/**
 * Rail row (DS NavItem): a 40px pill; the active item gets the lavender pill with a purple bold label
 * and icon (regular weight, never filled). Pass swatch instead of icon for folders.
 * Pass href to render a link (a real destination) instead of a button.
 */
export interface NavItemProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "onClick"> {
  icon?: string;
  /** Folder color, replaces icon */
  swatch?: string;
  label: React.ReactNode;
  count?: React.ReactNode;
  active?: boolean;
  /** Renders an <a href> instead of a <button> */
  href?: string;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
}

export function NavItem({ icon, swatch, label, count, active, href, className, ...rest }: NavItemProps): JSX.Element {
  const cls = ["gk-nav", swatch && "gk-nav--folder", className].filter(Boolean).join(" ");
  const body = (
    <>
      {swatch ? (
        <span className="gk-nav__swatch" style={{ background: swatch }} />
      ) : (
        icon && <Icon name={icon} />
      )}
      <span className="gk-nav__label">{label}</span>
      {count !== undefined && <span className="gk-nav__count">{count}</span>}
    </>
  );
  if (href !== undefined) {
    const { type: _type, disabled: _disabled, ...anchorRest } = rest;
    return (
      <a
        href={href}
        aria-current={active ? "page" : undefined}
        className={cls}
        {...(anchorRest as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
      >
        {body}
      </a>
    );
  }
  return (
    <button type="button" aria-current={active ? "page" : undefined} className={cls} {...rest}>
      {body}
    </button>
  );
}
